import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'app/contexts/auth/context';
import { useClientTheme } from 'contexts/ThemeContext';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { TAB_CONFIG } from './AppPresentation.constants';

function OnBoardingPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuthContext();
  const { logoPath, brandShortName } = useClientTheme();
  const roles = useMemo(
    () => (user?.roles || []).map((r) => String(r).toLowerCase()),
    [user]
  );
  const isAdmin = useIsAdmin();
  const [loading, setLoading] = useState(false);

  // All defined tabs that have a real route
  const allTabs = useMemo(
    () =>
      Object.keys(TAB_CONFIG).filter(
        (tabId) => TAB_CONFIG[tabId] && TAB_CONFIG[tabId].route
      ),
    []
  );

  const isTabAccessible = (tab) => {
    if (!tab) return false;
    if (isAdmin) return true;
    return tab.requiredRole ? roles.includes(tab.requiredRole) : false;
  };

  const accessibleTabIds = useMemo(
    () => allTabs.filter((id) => isTabAccessible(TAB_CONFIG[id])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allTabs, roles, isAdmin]
  );

  const hasAnyAccess = accessibleTabIds.length > 0;

  // Default to the first accessible tab so the panel shows something useful
  const [currentTab, setCurrentTab] = useState(
    accessibleTabIds[0] || allTabs[0]
  );
  const currentTabConfig = TAB_CONFIG[currentTab];
  const currentAccessible = isTabAccessible(currentTabConfig);

  const handleExploreClick = () => {
    if (!currentAccessible) return;
    if (!currentTabConfig?.route || currentTabConfig.route === '#') return;
    setLoading(true);
    window.location.href = currentTabConfig.route;
  };

  return (
    <div className="relative grid h-screen w-full grid-rows-2">
      <div className="bg-primary-600"></div>
      <div className="bg-gray-100"></div>

      <div className="absolute flex w-full items-center justify-center px-5 lg:my-36">
        <div className="w-full max-w-5xl">
          <div className="mt-5 rounded-lg bg-white px-6 py-6 text-lg shadow-xl sm:px-10 lg:px-20">
            <div className="mb-6 flex justify-center lg:hidden">
              <img
                className="h-16 object-contain"
                src={logoPath}
                alt={`${brandShortName} Logo`}
                onError={(e) => {
                  e.target.src = '/images/logos/logo-henddu-white.png';
                }}
              />
            </div>

            <div className="hidden justify-center lg:flex">
              <img
                className="h-18 object-contain"
                src={logoPath}
                alt={`${brandShortName} Logo`}
                onError={(e) => {
                  e.target.src = '/images/logos/logo-henddu-white.png';
                }}
              />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {allTabs.map((tabId) => {
                const tab = TAB_CONFIG[tabId];
                return (
                  <TabButton
                    key={tabId}
                    title={tab.title}
                    tabId={tab.id}
                    icon={tab.icon}
                    activeIcon={tab.activeIcon}
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                  />
                );
              })}
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 lg:gap-2">
              <div>
                <ul className="mb-10 ml-5 mt-5 list-disc text-gray-400">
                  {currentTabConfig?.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
                <button
                  onClick={handleExploreClick}
                  disabled={
                    loading ||
                    !currentAccessible ||
                    !currentTabConfig?.route ||
                    currentTabConfig.route === '#'
                  }
                  className={`rounded-md bg-primary-700 p-2 px-8 text-white transition-colors hover:bg-primary-800 disabled:opacity-50 ${
                    !currentAccessible ||
                    !currentTabConfig?.route ||
                    currentTabConfig.route === '#'
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer'
                  }`}
                  aria-label="Explore selected tab"
                  title={
                    !currentAccessible
                      ? t('onboarding.noAccess.description')
                      : undefined
                  }
                >
                  {loading ? t('common.loading') : t('onboarding.explore')}
                </button>
              </div>

              <div className="mt-6 flex justify-center lg:mt-0">
                <div className="relative h-[300px] w-[450px]">
                  <img
                    className={`h-full w-full object-contain transition ${
                      !currentAccessible ? 'blur-sm grayscale' : ''
                    }`}
                    src={currentTabConfig?.image}
                    alt={currentTabConfig?.imageAlt || 'Aperçu'}
                  />
                  {!currentAccessible && (
                    <div
                      role="status"
                      className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-black/40 px-6 text-center text-white backdrop-blur-sm"
                    >
                      <div className="flex size-12 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.8}
                          stroke="currentColor"
                          className="size-6"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-1.5 0h12a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5V12a1.5 1.5 0 0 1 1.5-1.5Z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm font-semibold">
                        {t('onboarding.noAccess.title')}
                      </p>
                      <p className="text-xs text-white/80">
                        {t('onboarding.noAccess.description')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!hasAnyAccess && (
        <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-center px-4 pt-4">
          <div
            role="alert"
            className="pointer-events-auto flex w-full max-w-3xl items-center justify-between gap-4 rounded-lg border border-info/40 bg-white px-5 py-3 shadow-lg dark:bg-dark-800"
          >
            <div className="text-left">
              <h2 className="text-base font-semibold text-info dark:text-info">
                {t('onboarding.noAccess.title')}
              </h2>
              <p className="mt-1 text-xs text-gray-700 dark:text-dark-200">
                {t('onboarding.noAccess.description')}
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              className="shrink-0 rounded-md bg-info px-4 py-1.5 text-sm font-medium text-white hover:bg-info/90 focus:outline-none focus:ring-2 focus:ring-info/50"
            >
              {t('noModuleAccess.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// TabButton component
const TabButton = ({
  title,
  icon,
  activeIcon,
  tabId,
  currentTab,
  setCurrentTab,
}) => {
  const isActive = currentTab === tabId;

  return (
    <button
      onClick={() => setCurrentTab(tabId)}
      className={`cursor-pointer border-b-4 px-6 py-4 transition-opacity ${
        isActive
          ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400'
          : 'border-gray-500 text-gray-400'
      }`}
    >
      <div className="flex justify-center">
        <img
          className="mb-2 w-10"
          src={isActive ? activeIcon : icon}
          alt={`${title} icon`}
        />
      </div>
      <span className="text-center capitalize">{title}</span>
    </button>
  );
};

export default OnBoardingPage;
